import React from "react";
import SiteShell from "@/components/SiteShell";

/** Privacy Policy (www.frazzlecrm.com/privacy). Drafted for launch — review before major changes. */

const EFFECTIVE = "July 19, 2026";

function H({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-8 text-[17px] font-bold text-slate-900">{children}</h2>
  );
}

export default function Privacy() {
  return (
    <SiteShell title="Privacy Policy — FrazzleCRM">
      <section className="mx-auto max-w-3xl px-6 pb-20 pt-16">
        <h1 className="text-[32px] font-extrabold tracking-tight text-slate-900">
          Privacy Policy
        </h1>
        <p className="mt-2 text-[13px] text-slate-400">
          Effective {EFFECTIVE}
        </p>
        <div className="mt-6 space-y-4 text-[14.5px] leading-relaxed text-slate-600">
          <p>
            This policy explains what FrazzleCRM (&ldquo;we&rdquo;,
            &ldquo;us&rdquo;) collects and how we use it, for both account
            holders and the contacts our customers store in the Service.
          </p>

          <H>1. What we collect</H>
          <p>
            <strong>Account data:</strong> your name, email, password (stored
            only as a salted hash), workspace names, and settings.{" "}
            <strong>Customer content:</strong> the contacts, messages,
            automations, maps, and files you put into your workspaces.{" "}
            <strong>Billing data:</strong> handled by Stripe — card numbers
            never touch our servers; we store only your Stripe customer and
            subscription identifiers and plan status.{" "}
            <strong>Usage data:</strong> standard server logs (IP address,
            timestamps, requested pages) kept for security and debugging.
          </p>

          <H>2. How we use it</H>
          <p>
            To operate the Service: authenticate you, run your automations,
            send the messages you configure through the credentials you
            connect, bill your subscription, respond to support requests, and
            keep the platform secure. We do not sell personal data, and we do
            not use your customer content for advertising or to train AI
            models.
          </p>

          <H>3. Your contacts&apos; data</H>
          <p>
            Our customers control the contact data they store; we process it
            only on their instructions (as their processor). If you believe a
            FrazzleCRM customer holds your data and you want it corrected or
            deleted, contact that business directly, or email us and
            we&apos;ll route the request.
          </p>

          <H>4. Third parties we rely on</H>
          <p>
            Stripe (payments), our hosting infrastructure, and — when you
            connect them — your own providers (SMTP services, Twilio,
            Facebook, external schedulers). Each processes data under its own
            terms; we share only what the integration needs to work.
          </p>

          <H>5. Cookies</H>
          <p>
            We use only essential cookies: session authentication and your
            active-workspace selection. No third-party advertising or
            analytics cookies.
          </p>

          <H>6. Retention and deletion</H>
          <p>
            Account data and customer content are kept while your account is
            active. When you delete your account (or ask us to), workspaces
            you own and their content are deleted within 30 days, except
            minimal records we must keep for legal or billing purposes.
          </p>

          <H>7. Security</H>
          <p>
            Passwords are hashed, connected credentials are stored encrypted
            (AES-256-GCM), traffic is TLS-encrypted, and access to production
            systems is restricted. No system is perfectly secure — if we
            learn of a breach affecting your data we will notify you without
            undue delay.
          </p>

          <H>8. Your rights</H>
          <p>
            Depending on where you live (e.g. GDPR, CCPA), you may have
            rights to access, correct, export, or delete your personal data.
            Email support@frazzlecrm.com and we&apos;ll honor verified requests
            within the required timeframes.
          </p>

          <H>9. Changes</H>
          <p>
            We&apos;ll post updates here and note the new effective date;
            material changes will also be emailed to account holders.
          </p>

          <H>10. Contact</H>
          <p>Privacy questions: support@frazzlecrm.com.</p>
        </div>
      </section>
    </SiteShell>
  );
}
